import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Filter, Download, Plus, Loader2, Inbox, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CreateOrganisationDialog } from "@/components/CreateOrganisationDialog";
import { CursorPager, type Cursors } from "@/components/ui/cursor-pager";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";

type OrganisationResponse = components["schemas"]["OrganisationResponse"];
type OrganisationSearchHit = components["schemas"]["OrganisationSearchHit"];

const PAGE_SIZE = 50;
// Server-side trigram search returns a lean hit, so searching swaps the table into a reduced
// column set rather than filtering the current page (which only ever saw PAGE_SIZE rows).
const SEARCH_LIMIT = 20;

function statusForOrg(o: OrganisationResponse): { label: "Active" | "Onboarding"; classes: string } {
  return o.hasBeenOnboarded
    ? { label: "Active",     classes: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" }
    : { label: "Onboarding", classes: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" };
}

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" });

export function OrganisationsPage() {
  const navigate = useNavigate();
  const { activeClient, activeClientId } = useAuth();
  const canImpersonate = activeClient?.role === "Owner" || activeClient?.role === "Admin";
  const [query, setQuery] = useState("");
  const [orgs, setOrgs] = useState<OrganisationResponse[] | null>(null);
  const [cursors, setCursors] = useState<Cursors | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const debouncedQuery = useDebouncedValue(query, 400);
  const searchTerm = debouncedQuery.trim();
  const searching = searchTerm.length > 0;
  const [hits, setHits] = useState<OrganisationSearchHit[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setCursor(null);
    setReloadTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOrgs(null);
    api
      .GET("/v1/organisations", {
        params: {
          query: {
            Limit: PAGE_SIZE,
            ...(cursor ? { Cursor: cursor } : {}),
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setError("Couldn't load organisations.");
          return;
        }
        setOrgs(data?.data ?? []);
        setCursors(data?.meta?.cursors ?? null);
      })
      .catch(() => { if (!cancelled) setError("Network error fetching organisations."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeClientId, reloadTick, cursor]);

  // Search hits every organisation the client owns, not just the page currently loaded.
  useEffect(() => {
    if (!activeClientId || !searching) {
      setHits(null);
      setSearchError(null);
      setSearchLoading(false);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    setSearchError(null);
    api
      .GET("/v1/organisations/search", { params: { query: { q: searchTerm, limit: SEARCH_LIMIT } } })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setSearchError("Couldn't search organisations.");
          return;
        }
        setHits(data ?? []);
      })
      .catch(() => { if (!cancelled) setSearchError("Network error searching organisations."); })
      .finally(() => { if (!cancelled) setSearchLoading(false); });
    return () => { cancelled = true; };
  }, [activeClientId, searching, searchTerm]);

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Organisations</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Use <span className="font-medium text-foreground">Open in sandbox</span> to impersonate a member of that organisation temporarily and see what they would see.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {searching
              ? searchLoading || hits === null
                ? "Searching…"
                : `${hits.length} match${hits.length === 1 ? "" : "es"} across all your organisations${hits.length === SEARCH_LIMIT ? " (top matches)" : ""}`
              : orgs === null
                ? "Loading…"
                : `${orgs.length} organisation${orgs.length === 1 ? "" : "s"} on this page`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, ID, email, tax no…"
              className="h-9 w-60 pl-8"
            />
          </div>
          <Button variant="outline" size="sm" disabled><Filter className="size-3.5" /> Status</Button>
          <Button variant="outline" size="sm" disabled><Download className="size-3.5" /> Export</Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} disabled={!activeClientId}>
            <Plus className="size-3.5" /> New organisation
          </Button>
        </div>
      </div>

      {activeClientId && (
        <CreateOrganisationDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={refresh}
        />
      )}

      {searching ? (
        searchError ? (
          <ErrorState message={searchError} />
        ) : searchLoading || hits === null ? (
          <LoadingState label="Searching organisations…" />
        ) : hits.length === 0 ? (
          <EmptyState clientName={activeClient?.clientName} hasFilter />
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead>Organisation</TableHead>
                  <TableHead>Contact email</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hits.map((h) => (
                  <TableRow
                    key={h.id}
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => h.id && navigate(`/organisations/${h.id}`)}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{h.name}</span>
                        <span className="font-mono text-xs text-muted-foreground">{h.id}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{h.contactEmail ?? "-"}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      {h.id && (
                        <OpenInSandboxButton
                          organisationId={h.id}
                          organisationName={h.name}
                          canImpersonate={canImpersonate}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : error ? (
        <ErrorState message={error} />
      ) : loading ? (
        <LoadingState />
      ) : (orgs?.length ?? 0) === 0 ? (
        <EmptyState clientName={activeClient?.clientName} hasFilter={false} />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead>Organisation</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(orgs ?? []).map((o) => {
                const status = statusForOrg(o);
                return (
                  <TableRow
                    key={o.id}
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => o.id && navigate(`/organisations/${o.id}`)}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{o.name}</span>
                        <span className="font-mono text-xs text-muted-foreground">{o.id}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={status.classes}>{status.label}</Badge>
                    </TableCell>
                    <TableCell><span className="font-mono text-xs">{o.country}</span></TableCell>
                    <TableCell className="text-muted-foreground">{o.industryDisplayName ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {o.createdAt ? dateFmt.format(new Date(o.createdAt)) : "—"}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      {o.id && (
                        <OpenInSandboxButton
                          organisationId={o.id}
                          organisationName={o.name}
                          canImpersonate={canImpersonate}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {!searching && !error && !loading && orgs && orgs.length > 0 && (
        <CursorPager cursors={cursors} onNavigate={setCursor} />
      )}
    </div>
  );
}

function OpenInSandboxButton({
  organisationId, organisationName, canImpersonate,
}: { organisationId: string; organisationName: string; canImpersonate: boolean }) {
  const [busy, setBusy] = useState(false);

  async function open() {
    setBusy(true);
    try {
      const { data, error, response } = await api.POST("/v1/auth/sandbox-handoff", {
        body: { organisationId },
      });
      if (error || !data?.sandboxUrl) {
        if (response?.status === 403) {
          toast.error(`Forbidden: You don't have access to ${organisationName} in sandbox.`);
        } else {
          toast.error("Couldn't open sandbox handoff. Try again.");
        }
        return;
      }
      window.open(data.sandboxUrl, "_blank", "noopener,noreferrer");
    } finally {
      setBusy(false);
    }
  }

  const button = (
    <Button
      variant="outline"
      size="sm"
      onClick={open}
      disabled={busy || !canImpersonate}
      title={canImpersonate ? "Open this organisation in sandbox" : undefined}
    >
      {busy ? <Spinner className="size-3.5" /> : <ExternalLink className="size-3.5" />}
      Open in sandbox
    </Button>
  );

  if (canImpersonate) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-block" tabIndex={0}>{button}</span>
      </TooltipTrigger>
      <TooltipContent>
        You must be an Admin or Owner to impersonate the organisation.
      </TooltipContent>
    </Tooltip>
  );
}

function LoadingState({ label = "Loading organisations…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border py-16 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> {label}
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
      {message}
    </div>
  );
}

function EmptyState({ clientName, hasFilter }: { clientName?: string; hasFilter: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border py-16 text-center">
      <Inbox className="size-7 text-muted-foreground" />
      {hasFilter ? (
        <p className="text-sm text-muted-foreground">No organisations match your search.</p>
      ) : (
        <>
          <p className="text-sm font-medium">No organisations yet for {clientName ?? "this client"}.</p>
          <p className="max-w-sm text-xs text-muted-foreground">
            Provision your first organisation via the API or use the "New organisation" button (coming soon).
          </p>
        </>
      )}
    </div>
  );
}
